import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api/index";
import { ApiMutationError } from "views_shared/app/javascript/api/api";
import ProductImagePlaceholder from "./products/reusable/product_image_placeholder";

// Matches the file input's `accept` attribute below -- that attribute is only a picker hint (a
// user can still drag-drop or paste an unsupported file), so the same list is enforced here too.
// Keyed by extension since .jpg/.jpeg share a MIME type.
const EXTENSION_MIME_TYPES = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".svg": "image/svg+xml",
};

// Extension decides whether the file is even a candidate; the MIME type (browser/OS-reported,
// not just trusted) then has to agree with it. Some browsers and OSes omit `file.type` entirely,
// so an empty type doesn't fail a file the extension already accepted -- but a *present* type
// that names a different format (a renamed PDF, a mismatched extension/type pair) does.
// Exported so other owner-image uploaders (e.g. the company banner) can reuse the same guard
// rather than duplicating the accepted-format list.
export const hasAcceptedFileType = (file) => {
  const filename = file.name.toLowerCase();
  const extension = Object.keys(EXTENSION_MIME_TYPES).find((candidate) => filename.endsWith(candidate));
  if (!extension) return false;
  if (!file.type) return true;

  return file.type === EXTENSION_MIME_TYPES[extension];
};

// Convert file to base64 using FileReader. Exported (and hoisted to module scope, out of the
// component below) for the same reason as hasAcceptedFileType -- other owner-image uploaders need
// the identical conversion.
export const fileToBase64WithName = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Return an object with both the base64 data and the filename.
      resolve({ base64: reader.result, filename: file.name });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Which API client owns an entity's profile image, keyed by the `imgOwnerType` every call site
// already passes. Adding a consumer is one entry here rather than a branch inside the component.
const IMAGE_CLIENTS = {
  company: qapi.companies,
  expert: qapi.experts,
  media_outlet: qapi.mediaOutlets,
  product: qapi.products,
  user_profile: qapi.userProfiles,
};

// The image clients resolve to `{ data }` or `{ error }` rather than throwing, so a failed mutation
// has to be turned into one before anything downstream treats it as a save that happened.
const mutate = async (call) => {
  const result = await call();
  if (result?.error) throw new ApiMutationError(result.error);
  return result;
};

function ProfileImage({
  editable = false,
  profileImageUrl = undefined,
  imgOwnerId = undefined,
  imgOwnerName = undefined,
  imgOwnerType = undefined,
  onChange = undefined,
  framed = false,
  renderImage = undefined,
  openPickerSignal = 0,
}) {
  const [imgUrl, setImgUrl] = useState(profileImageUrl);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  // A failed <img> load (off-site CDN down, DiceBear unreachable) falls back to the existing
  // placeholder branches below rather than leaving a broken-image icon. Stores the failed URL
  // itself (not a boolean) so the failed state derives synchronously during render whenever
  // imgUrl mutates (upload, delete) -- no reset effect, no one-frame stale-placeholder flash.
  const [failedImgUrl, setFailedImgUrl] = useState(null);
  const imgFailed = Boolean(imgUrl) && failedImgUrl === imgUrl;
  const inputFileRef = useRef(null);
  const client = IMAGE_CLIENTS[imgOwnerType];

  // The provider hands out a freshly read entity after every authoritative read, so the prop is the
  // authority on what the image is now. Seeding state once at mount would leave a saved (or deleted)
  // image showing whatever it showed when the component first rendered.
  useEffect(() => {
    setImgUrl(profileImageUrl);
  }, [profileImageUrl]);

  const handleHover = (pointerIsInside) => {
    setIsHovering(pointerIsInside);
  };

  const openFileInput = () => {
    inputFileRef.current.click();
  };

  // Lets an outside affordance open this control's file picker by bumping openPickerSignal, reusing
  // the one hidden file input. Skip the mount pass so it never opens on load; the ref may be null
  // when the control isn't rendered.
  const initialOpenPickerSignal = useRef(openPickerSignal);
  useEffect(() => {
    if (openPickerSignal === initialOpenPickerSignal.current) return;
    if (inputFileRef.current) inputFileRef.current.click();
  }, [openPickerSignal]);

  const updateProfileImage = async (event) => {
    const { files = [] } = event.target;
    if (files.length > 0) {
      const file = files[0];

      if (!hasAcceptedFileType(file)) {
        // eslint-disable-next-line no-alert
        alert("File not supported. Please upload a PNG, JPG, JPEG, or SVG file.");
        inputFileRef.current.value = ""; // allow re-selecting the same (rejected) file
        return;
      }

      setSavingInProgress(true);

      try {
        // Convert the file to base64 + get the filename
        const { base64, filename } = await fileToBase64WithName(file);

        // Pass both the base64 and filename in the attributes
        const attributes = {
          image_cloudinary: base64,
          original_filename: filename,
        };

        // A generated placeholder means the entity has no image row yet, so this is a create.
        const defaultImage = imgUrl?.startsWith("https://api.dicebear.com/");

        // Call the relevant API endpoint
        if (defaultImage) {
          await mutate(() => client.createProfileImageById(imgOwnerId, attributes));
        } else {
          await mutate(() => client.updateProfileImageById(imgOwnerId, attributes));
        }
        qahoy.track("ProfileImage#updateProfileImage");
        // onChange is the caller's "this saved, refetch" signal, so it only belongs on the success
        // path — firing it from a finally block reported every failed upload as a save. It is also
        // awaited: it is the authoritative reload, and the props this component branches on (create
        // vs. update) only tell the truth once it has landed. Re-enabling the controls before then
        // lets a second upload take the create path again against the pre-save image.
        await onChange?.();
      } catch (error) {
        // eslint-disable-next-line no-alert
        alert("Something went wrong. Please try again later.");
      } finally {
        setSavingInProgress(false);
      }
    }
  };

  const deleteProfileImage = async () => {
    setSavingInProgress(true);

    try {
      await mutate(() => client.deleteProfileImageById(imgOwnerId));
      qahoy.track("ProfileImage#deleteProfileImage");
      setImgUrl(null);
      // Awaited for the same reason as the save path: until the reload lands the props still carry
      // the deleted image, and an upload started before then would PATCH a row that is gone.
      await onChange?.();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert("Something went wrong. Please try again later.");
    } finally {
      setSavingInProgress(false);
    }
  };

  const editLogoClassNames = `${
    isHovering || savingInProgress || !imgUrl ? "fadeIn " : ""
  } position-absolute d-inline-block btn-edit-organization-logo animated faster`;

  const defaultImage = imgUrl?.startsWith("https://api.dicebear.com/");

  // Products with no image of their own fall back to the tag-tile placeholder; other owner types
  // keep showing the generated dicebear default. A failed load counts the same as no image.
  const showProductPlaceholder = imgOwnerType === "product" && (!imgUrl || defaultImage || imgFailed);

  const framedWrapperStyle = {
    position: "relative",
    width: "160px",
    height: "160px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px",
    border: "2px solid #D2D3D4",
    boxShadow:
      "0px 2px 2px -1px rgba(10, 13, 18, 0.04), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 12px 16px -4px rgba(10, 13, 18, 0.08)",
    background: "#FFFFFF",
    overflow: "hidden",
    margin: "auto",
  };

  const unframedWrapperStyle = {
    position: "relative",
    borderRadius: "3.2px",
    overflow: "hidden",
    maxHeight: "160px",
    maxWidth: "160px",
    margin: "auto",
  };

  // `renderImage` hands rendering of the image itself to the caller (e.g. AvatarWithLabel's ringed,
  // labeled person avatar) while this component keeps owning every bit of the control logic (hover
  // overlay, file input, saving state, delete confirm, default-image detection, error handling). The
  // wrapper then only needs to be a positioned box the hover-revealed controls can anchor to — the
  // `framed`/unframed geometry below is specifically the org-logo look, which a person avatar must
  // not inherit.
  let wrapperStyle = unframedWrapperStyle;
  if (renderImage) {
    wrapperStyle = { position: "relative", display: "inline-block" };
  } else if (framed) {
    wrapperStyle = framedWrapperStyle;
  }

  const imgStyle = framed
    ? { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }
    : { maxWidth: "100%", minWidth: "100px" };

  return (
    <div
      className="organization-logo"
      style={wrapperStyle}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      {renderImage && renderImage(imgUrl)}

      {!renderImage && showProductPlaceholder && (
        <div style={{ width: "100%", height: "100%", padding: framed ? 16 : 0, boxSizing: "border-box" }}>
          <ProductImagePlaceholder iconFontSize="2.5rem" />
        </div>
      )}

      {!renderImage && !showProductPlaceholder && imgUrl && !imgFailed && (
        <img alt={imgOwnerName} src={imgUrl} style={imgStyle} onError={() => setFailedImgUrl(imgUrl)} />
      )}

      {!renderImage && !showProductPlaceholder && (!imgUrl || imgFailed) && (
        <i
          className="fa-solid fa-city fa-fw fa-8x"
          style={{
            color: "lightgrey",
            margin: "16px 0",
            width: "100%",
          }}
        />
      )}

      {editable && imgOwnerId && client && (
        <div
          className={editLogoClassNames}
          style={{
            bottom: 5,
            right: 5,
            visibility: isHovering || savingInProgress || !imgUrl ? "visible" : "hidden",
          }}
        >
          <div className="btn-group btn-group-sm" role="group" aria-label="organization-logo-admin-buttons">
            {savingInProgress && (
              <button type="button" className="btn btn-primary">
                <i className="fa-solid fa-spinner fa-spin fa-fw" />
              </button>
            )}

            {!savingInProgress && (
              <>
                <button type="button" className="btn btn-primary" onClick={openFileInput}>
                  <i className="fa-solid fa-pencil-alt fa-fw" />
                </button>

                {imgUrl && !defaultImage && (
                  <button type="button" className="btn btn-danger" onClick={deleteProfileImage}>
                    <i className="fa-solid fa-trash fa-fw" />
                  </button>
                )}
              </>
            )}
          </div>

          <input
            accept=".png, .jpg, .jpeg, .svg"
            id="logo_input"
            onChange={updateProfileImage}
            ref={inputFileRef}
            style={{ display: "none" }}
            type="file"
          />
        </div>
      )}
    </div>
  );
}

ProfileImage.propTypes = {
  editable: PropTypes.bool,
  imgOwnerId: PropTypes.number,
  imgOwnerName: PropTypes.string,
  imgOwnerType: PropTypes.string,
  onChange: PropTypes.func,
  profileImageUrl: PropTypes.string,
  framed: PropTypes.bool,
  renderImage: PropTypes.func,
  openPickerSignal: PropTypes.number,
};

export default ProfileImage;
